// services/bar/InventoryReportService.js
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { 
formatNumberWithComma,
getCategoriesNameById,
formatCurrency
} from '../../utils/helpers';

// Helper functions
const getSupplierName = (supplierId, supplierNamesData) => {
if (!supplierNamesData || !Array.isArray(supplierNamesData)) return 'Unknown';
const supplier = supplierNamesData.find(s => parseInt(s.id) === parseInt(supplierId));
return supplier ? supplier.name : 'Unknown';
};

const getStockName = (stockId, stockNamesData) => {
if (!stockNamesData || !Array.isArray(stockNamesData)) return 'Unknown';
const stock = stockNamesData.find(s => parseInt(s.id) === parseInt(stockId));
return stock ? stock.name : 'Unknown';
};

const formatDate = (dateString) => {
if (!dateString) return 'N/A';
try {
    return new Date(dateString).toLocaleDateString();
} catch (error) {
    return 'Invalid Date';
}
};

// Calculate profit for an item
const calculateProfit = (item) => {
return (item.selling - item.cost) * item.quantity;
};

// Calculate total value for an item
const calculateTotalValue = (item) => {
return item.quantity * item.selling;
};

// Helper function to split text into multiple lines for PDF
const splitTextIntoLines = (doc, text, maxWidth) => {
if (!text) return [''];
const words = String(text).split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for PDF - UPDATED FOR LANDSCAPE
const drawReportTable = (doc, data, startY, referenceData, reportType) => {
const pageWidth = doc.internal.pageSize.width;
const pageHeight = doc.internal.pageSize.height;
const margin = 20;
const rowHeight = 12;

// Column widths for landscape mode
let colWidths = [];
let headers = [];

switch (reportType) {
    case 'detailed':
    default:
    colWidths = [10, 20, 30, 40, 20, 20, 20, 25, 25, 20];
    headers = ['#', 'Date', 'Supplier', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    break;
}

let currentY = startY;

// Check if we need a new page
if (currentY + 50 > pageHeight - 30) {
    doc.addPage('l');
    currentY = margin;
}

// Draw table header
doc.setFillColor(25, 118, 210);
doc.setTextColor(255, 255, 255);
doc.setFont(undefined, 'bold');

let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 8);
    xPos += colWidths[index];
});

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((item, rowIndex) => {
    // Prepare row data
    const rowData = [
    (rowIndex + 1).toString(),
    formatDate(item.stock_date),
    item.supplier_name || getSupplierName(item.supplier_id, referenceData.supplierNames) || 'Unknown',
    item.stock_name || getStockName(item.stock_id, referenceData.stockNames) || 'Unknown',
    formatNumberWithComma(item.quantity || 0),
    formatCurrency(item.cost || 0),
    formatCurrency(item.selling || 0),
    formatCurrency((item.quantity || 0) * (item.cost || 0)),
    formatCurrency((item.quantity || 0) * (item.selling || 0)),
    formatCurrency(calculateProfit(item))
    ];

    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = rowData.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > pageHeight - 30) {
    doc.addPage('l');
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 8);
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
    doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
    if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 8 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Excel export for Bar Inventory Report
export const exportToExcel = async (reportData, filters, referenceData) => {
try {
    const { summary, details, topSuppliers, topStockItems, lowQuantityItems, highValueItems } = reportData;
    const { supplierNames = [], stockNames = [] } = referenceData;

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
    ['BAR INVENTORY REPORT SUMMARY'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['OVERALL SUMMARY'],
    ['Total Items:', summary.total_items],
    ['Total Quantity:', summary.total_quantity],
    ['Total Cost Value:', formatCurrency(summary.total_cost_value)],
    ['Total Selling Value:', formatCurrency(summary.total_selling_value)],
    ['Total Profit:', formatCurrency(summary.total_profit)],
    ['Average Cost per Item:', formatCurrency(summary.average_cost_per_item)],
    ['Average Selling per Item:', formatCurrency(summary.average_selling_per_item)],
    ['Suppliers Count:', summary.suppliers_count],
    ['Stock Items Count:', summary.stock_items_count],
    [],
    ['PROFITABILITY ANALYSIS'],
    ['Profit Margin:', summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    ['Cost Percentage:', summary.total_selling_value > 0 ? `${((summary.total_cost_value / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    [],
    ['APPLIED FILTERS'],
    filters.dateFrom && filters.dateTo ? [`Date Range: ${filters.dateFrom} to ${filters.dateTo}`] : [],
    filters.supplier_id ? [`Supplier: ${getSupplierName(filters.supplier_id, supplierNames)}`] : [],
    filters.stock_id ? [`Stock Item: ${getStockName(filters.stock_id, stockNames)}`] : [],
    filters.min_quantity ? [`Minimum Quantity: ${filters.min_quantity}`] : [],
    filters.max_quantity ? [`Maximum Quantity: ${filters.max_quantity}`] : []
    ].filter(row => row.length > 0);

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Detailed Data Sheet - Show all attributes
    if (details && details.length > 0) {
    const detailedHeaders = [
        'ID', 
        'Stock Date', 
        'Supplier ID', 
        'Supplier Name', 
        'Stock ID', 
        'Stock Name', 
        'Quantity', 
        'Cost Price', 
        'Selling Price', 
        'Total Cost', 
        'Total Selling', 
        'Profit'
    ];
    
    const detailedData = details.map((item) => [
        item.id || 'N/A',
        formatDate(item.stock_date),
        item.supplier_id || 'N/A',
        getSupplierName(item.supplier_id, supplierNames),
        item.stock_id || 'N/A',
        getStockName(item.stock_id, stockNames),
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        (item.quantity || 0) * (item.cost || 0),
        (item.quantity || 0) * (item.selling || 0),
        calculateProfit(item)
    ]);

    const detailedSheet = XLSX.utils.aoa_to_sheet([detailedHeaders, ...detailedData]);
    
    // Set column widths
    const wscols = [
        { wch: 10 },  // ID
        { wch: 15 },  // Stock Date
        { wch: 12 },  // Supplier ID
        { wch: 25 },  // Supplier Name
        { wch: 12 },  // Stock ID
        { wch: 30 },  // Stock Name
        { wch: 12 },  // Quantity
        { wch: 12 },  // Cost Price
        { wch: 15 },  // Selling Price
        { wch: 15 },  // Total Cost
        { wch: 15 },  // Total Selling
        { wch: 15 }   // Profit
    ];
    detailedSheet['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(workbook, detailedSheet, 'Detailed Data');
    }

    // Top Suppliers Sheet
    if (topSuppliers && topSuppliers.length > 0) {
    const supplierHeaders = ['Supplier', 'Items Count', 'Total Quantity', 'Total Cost', 'Total Value', 'Total Profit'];
    const supplierData = topSuppliers.map(supplier => [
        supplier.supplier_name || 'Unknown',
        supplier.items_count || 0,
        supplier.total_quantity || 0,
        supplier.total_cost || 0,
        supplier.total_value || 0,
        supplier.total_profit || 0
    ]);

    const supplierSheet = XLSX.utils.aoa_to_sheet([supplierHeaders, ...supplierData]);
    XLSX.utils.book_append_sheet(workbook, supplierSheet, 'Top Suppliers');
    }

    // High Value Items Sheet
    if (highValueItems && highValueItems.length > 0) {
    const highValueHeaders = ['Stock Item', 'Supplier', 'Quantity', 'Cost', 'Selling', 'Total Value', 'Profit'];
    const highValueData = highValueItems.map(item => [
        item.stock_name || 'Unknown',
        item.supplier_name || 'Unknown',
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        item.total_value || 0,
        item.profit || 0
    ]);

    const highValueSheet = XLSX.utils.aoa_to_sheet([highValueHeaders, ...highValueData]);
    XLSX.utils.book_append_sheet(workbook, highValueSheet, 'High Value Items');
    }

    // Low Quantity Alerts Sheet
    if (lowQuantityItems && lowQuantityItems.length > 0) {
    const lowQtyHeaders = ['Stock Item', 'Supplier', 'Quantity', 'Cost', 'Selling', 'Status'];
    const lowQtyData = lowQuantityItems.map(item => [
        item.stock_name || 'Unknown',
        item.supplier_name || 'Unknown',
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        (item.quantity || 0) <= 0 ? 'Out of Stock' : (item.quantity || 0) <= 10 ? 'Low Stock' : 'Normal'
    ]);

    const lowQtySheet = XLSX.utils.aoa_to_sheet([lowQtyHeaders, ...lowQtyData]);
    XLSX.utils.book_append_sheet(workbook, lowQtySheet, 'Low Quantity Alerts');
    }

    // Generate file name
    const fileName = `bar_inventory_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Bar Inventory Report Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Bar Inventory Report
export const exportToPDF = async (reportData, filters, referenceData) => {
return new Promise((resolve, reject) => {
    try {
    const { summary, details } = reportData;
    
    // Create PDF in landscape mode
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let currentY = margin;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text("BAR INVENTORY ANALYSIS REPORT", pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Date and time
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 8;
    
    // Summary
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Items: ${formatNumberWithComma(summary.total_items || 0)}`, margin, currentY);
    currentY += 6;
    doc.text(`Total Quantity: ${formatNumberWithComma(summary.total_quantity || 0)}`, margin + 60, currentY);
    currentY += 6;
    doc.text(`Total Cost Value: ${formatCurrency(summary.total_cost_value || 0)}`, margin, currentY);
    currentY += 6;
    doc.text(`Total Selling Value: ${formatCurrency(summary.total_selling_value || 0)}`, margin + 60, currentY);
    currentY += 6;
    doc.text(`Total Profit: ${formatCurrency(summary.total_profit || 0)}`, margin, currentY);
    currentY += 6;
    doc.text(`Profit Margin: ${summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'}`, margin + 60, currentY);
    currentY += 15;
    
    // Apply filters text
    const filterTexts = [];
    if (filters.dateFrom) filterTexts.push(`From: ${filters.dateFrom}`);
    if (filters.dateTo) filterTexts.push(`To: ${filters.dateTo}`);
    if (filters.supplier_id) filterTexts.push(`Supplier: ${getSupplierName(filters.supplier_id, referenceData?.supplierNames || [])}`);
    if (filters.stock_id) filterTexts.push(`Stock: ${getStockName(filters.stock_id, referenceData?.stockNames || [])}`);
    if (filters.min_quantity) filterTexts.push(`Min Qty: ${filters.min_quantity}`);
    if (filters.max_quantity) filterTexts.push(`Max Qty: ${filters.max_quantity}`);
    
    if (filterTexts.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const filtersText = `Filters: ${filterTexts.join(', ')}`;
        const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
        
        filtersLines.forEach(line => {
        if (currentY > pageHeight - 40) {
            doc.addPage('l');
            currentY = margin;
        }
        doc.text(line, margin, currentY);
        currentY += 6;
        });
        
        currentY += 10;
    }
    
    // Detailed Data Table
    if (details && details.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(25, 118, 210);
        doc.text("DETAILED INVENTORY DATA", pageWidth / 2, currentY, { align: 'center' });
        currentY += 10;
        
        // Draw the table
        drawReportTable(doc, details, currentY, referenceData, 'detailed');
    } else {
        doc.setFontSize(12);
        doc.setTextColor(25, 118, 210);
        doc.text("NO DATA AVAILABLE", pageWidth / 2, currentY, { align: 'center' });
        currentY += 10;
        
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        doc.text("No inventory items found matching the current filters", pageWidth / 2, currentY, { align: 'center' });
    }
    
    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }
    
    const fileName = `bar_inventory_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    resolve({ success: true, fileName });
    } catch (error) {
    console.error('Bar Inventory Report PDF export failed:', error);
    reject(new Error('Failed to export to PDF: ' + error.message));
    }
});
};

// CSV export for Bar Inventory Report - Show all attributes
export const exportToCSV = async (reportData, filters, referenceData) => {
try {
    const { summary, details } = reportData;
    const { supplierNames = [], stockNames = [] } = referenceData;

    // Create summary CSV
    const summaryCsv = [
    ['BAR INVENTORY REPORT SUMMARY'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['Metric', 'Value'],
    ['Total Items', summary.total_items],
    ['Total Quantity', summary.total_quantity],
    ['Total Cost Value', formatCurrency(summary.total_cost_value)],
    ['Total Selling Value', formatCurrency(summary.total_selling_value)],
    ['Total Profit', formatCurrency(summary.total_profit)],
    ['Average Cost per Item', formatCurrency(summary.average_cost_per_item)],
    ['Average Selling per Item', formatCurrency(summary.average_selling_per_item)],
    ['Suppliers Count', summary.suppliers_count],
    ['Stock Items Count', summary.stock_items_count],
    ['Profit Margin', summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    [],
    ['FILTERS APPLIED'],
    filters.dateFrom && filters.dateTo ? ['Date Range', `${filters.dateFrom} to ${filters.dateTo}`] : [],
    filters.supplier_id ? ['Supplier', getSupplierName(filters.supplier_id, supplierNames)] : [],
    filters.stock_id ? ['Stock Item', getStockName(filters.stock_id, stockNames)] : []
    ].filter(row => row.length > 0);

    // Create detailed CSV with all attributes
    let detailedCsv = [];
    if (details && details.length > 0) {
    const detailedHeaders = [
        'ID', 
        'Stock Date', 
        'Supplier ID', 
        'Supplier Name', 
        'Stock ID', 
        'Stock Name', 
        'Quantity', 
        'Cost Price', 
        'Selling Price', 
        'Total Cost', 
        'Total Selling', 
        'Profit'
    ];
    
    detailedCsv = [
        ['DETAILED INVENTORY DATA'],
        [],
        detailedHeaders,
        ...details.map(item => [
        item.id || '',
        formatDate(item.stock_date),
        item.supplier_id || '',
        getSupplierName(item.supplier_id, supplierNames),
        item.stock_id || '',
        getStockName(item.stock_id, stockNames),
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        (item.quantity || 0) * (item.cost || 0),
        (item.quantity || 0) * (item.selling || 0),
        calculateProfit(item)
        ])
    ];
    }

    // Combine all CSV data
    const allCsvData = [...summaryCsv, [], ...detailedCsv];
    
    const csvContent = allCsvData
    .map(row => row.map(cell => {
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
        return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
    }).join(','))
    .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bar_inventory_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Bar Inventory Report CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};